import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import { BASE_URL, IMAGE_PATHS } from '../../../../constants/paths.constants';
import { borderDarkGrey, purple, transparentYellowBox, universalStyles, yellow } from '../../../../app/theme';
import FindingsPanel from '../../../micro/FindingsPanel/FindingsPanel';
import LearningCheckQuestions from '../../../micro/learningCheck/LearningCheckQuestions';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  headerImage: {
    display: 'block',
    width: '100%',
  },
  pageSubtitle: {
    marginTop: 43,
    marginBottom: 25,
  },
  column: {
    width: '48%',
  },
  measurementRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  measurement: {
    color: purple,
    fontSize: 36,
    fontWeight: 'bold',
    marginRight: 29,
    whiteSpace: 'nowrap',
    lineHeight: 1,
  },
  rightMeasurement: {
    color: yellow,
  },
  headerRow: {
    marginTop: 50,
    width: 612,
    overflowX: 'visible',
    borderBottom: `1px solid ${borderDarkGrey}`,
    paddingBottom: 21,
    marginBottom: 20,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    display: 'inline',
    width: '50vw',
    minWidth: 612,
    marginBottom: 0,
  },
  subtitle: {
    fontWeight: 'normal',
    fontStyle: 'italic',
  },
  text: {
    margin: '0 0 25px',
  },
  footnote: {
    fontSize: 14,
    color: borderDarkGrey,
    marginBottom: 30,
  },
  yellowBox: {
    margin: '0 25px 33px -25px',
    width: '100%',
    padding: '21px 25px',
    backgroundColor: transparentYellowBox,
  },
  rowWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  columnWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
  },
  row: {
    width: '100%',
    marginBottom: 30,
  },
  image: {
    maxWidth: '100%',
    height: 'auto',
  },
  fullImage: {
    width: '90%',
    height: 'auto',
    margin: '0 5%',
  },
  smallImage: {
    maxWidth: '75%',
    height: 'auto',
  },
  rowTitle: {
    marginBottom: 12,
  },
}));

type InterpretationPage15Props = {
  setNextDisabled: (disabled: boolean) => void;
};

const InterpretationPage15 = ({ setNextDisabled }: InterpretationPage15Props): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.grayBox}>
        <img
          className={classes.headerImage}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[1].INTERPRETATION_STEP_3.path}`}
          alt={`${IMAGE_PATHS.STEPS[1].INTERPRETATION_STEP_3.alt}`}
        />
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 3:</Typography>
          <Typography variant="h3">Assess and Evaluate Axumin Uptake in Various Settings (continued)</Typography>
          <Typography variant="h5" className={classes.pageSubtitle}>
            Lymph Nodes
          </Typography>
          <div className={classes.columnWrapper}>
            <div className={classes.column}>
              <div className={classes.measurementRow}>
                <Typography className={classes.measurement}>≥ 1cm</Typography>
                <Typography variant="body1" className={classes.text}>
                  Lymph nodes in a typical site of recurrence of prostate cancer, greater than or equal to 1 cm (maximum
                  dimension)
                </Typography>
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.measurementRow}>
                <Typography className={classNames(classes.measurement, classes.rightMeasurement)}>&lt; 1cm</Typography>
                <Typography variant="body1" className={classes.text}>
                  Lymph nodes in a typical site of recurrence of prostate cancer, less than 1 cm (maximum dimension)
                </Typography>
              </div>
            </div>
          </div>
          <div className={classes.columnWrapper}>
            <div className={classes.column}>
              <img
                src={`${BASE_URL}${IMAGE_PATHS.CHART_LEFT.path}`}
                alt={IMAGE_PATHS.CHART_LEFT.alt}
                className={classes.image}
              />
              <Typography className={classes.footnote}>
                *Uptake not meeting threshold for malignancy (equivocal) may require follow-up and clinical correlation.
              </Typography>
              <ul className={classes.list}>
                <li>
                  Uptake in lymph nodes ≥ 1 cm in a typical site of recurrence of prostate cancer should have a higher
                  threshold for positivity relative to lymph nodes &lt; 1 cm
                </li>
                <ul className={classNames(classes.list, classes.nestedList)}>
                  <li>
                    If a node ≥ 1 cm does not meet this threshold of greater than or equal to bone marrow (including
                    those approaching, but not reaching, bone marrow), it should be reported as such but requires
                    follow-up and clinical correlation
                  </li>
                </ul>
              </ul>
            </div>
            <div className={classes.column}>
              <img
                src={`${BASE_URL}${IMAGE_PATHS.CHART_RIGHT.path}`}
                alt={IMAGE_PATHS.CHART_RIGHT.alt}
                className={classes.image}
              />
              <Typography className={classes.footnote}>
                *Uptake not meeting threshold for malignancy (equivocal) may require follow-up and clinical correlation.
              </Typography>
              <ul className={classes.list}>
                <li>
                  Uptake in a lymph node &lt; 1 cm in a typical site of recurrence of prostate cancer has a lower
                  threshold for positivity, which is considered significantly greater than blood pool (ie, close to,
                  equal to, or greater than marrow)
                </li>
                <ul className={classNames(classes.list, classes.nestedList)}>
                  <li>
                    If a node &lt; 1 cm does not meet this threshold, it should be reported as such but requires
                    follow-up and clinical correlation
                  </li>
                </ul>
              </ul>
            </div>
          </div>
          <Typography variant="h5" className={classes.pageSubtitle}>
            Lymph Nodes
          </Typography>
          <img
            className={classNames(classes.image, classes.fullImage)}
            src={`${BASE_URL}${IMAGE_PATHS.TABLE_LYMPHNODES.path}`}
            alt={IMAGE_PATHS.TABLE_LYMPHNODES.alt}
          />
          <div className={classes.headerRow}>
            <Typography variant="h5" className={classes.title}>
              Please review the images and findings below
            </Typography>
            <Typography variant="h5" className={classNames(classes.title, classes.subtitle)}>
              See arrows for the location of the lesions and review findings
            </Typography>
          </div>
          <Typography variant="body1" className={classes.text}>
            <em>Subject presented with rising PSA (2.31 ng/mL), post-radiotherapy and brachytherapy</em>
          </Typography>
          <div className={classNames(classes.yellowBox, classes.rowWrapper)}>
            <div className={classes.row}>
              <Typography variant="body1" className={classes.rowTitle}>
                <b>Cranial to caudal view</b>
              </Typography>
              <img
                className={classes.image}
                src={`${BASE_URL}${IMAGE_PATHS.CRANIAL_CAUDAL_STACKVIEW.path}`}
                alt={IMAGE_PATHS.CRANIAL_CAUDAL_STACKVIEW.alt}
              />
            </div>
          </div>
          <Typography variant="body1">
            <b>Findings:</b>
          </Typography>
          <ul className={classes.list}>
            <li>MR negative for extra-prostatic disease</li>
            <li>
              Image interpretation: positive sub-cm right common iliac and obturator nodes (yellow arrows),
              histopathological sampling confirmed malignancy
            </li>
          </ul>
          <div className={classes.headerRow}>
            <Typography variant="h5" className={classes.title}>
              Please review the images and findings below
            </Typography>
            <Typography variant="h5" className={classNames(classes.title, classes.subtitle)}>
              See arrows for the location of the lesions and review findings
            </Typography>
          </div>
          <Typography variant="body1" className={classes.text}>
            <em>Subject presented with rising PSA post-therapy</em>
          </Typography>
          <div className={classNames(classes.yellowBox, classes.rowWrapper)}>
            <div className={classes.row}>
              <Typography variant="body1" className={classes.rowTitle}>
                <b>Cranial to caudal view</b>
              </Typography>
              <img
                className={classes.image}
                src={`${BASE_URL}${IMAGE_PATHS.CRANIAL_CAUDAL_FINDINGS.path}`}
                alt={IMAGE_PATHS.CRANIAL_CAUDAL_FINDINGS.alt}
              />
            </div>
            <div className={classes.row}>
              <Typography variant="body1" className={classes.rowTitle}>
                <b>Coronal view</b>
              </Typography>
              <img
                className={classes.image}
                src={`${BASE_URL}${IMAGE_PATHS.CORONAL_VIEW_4.path}`}
                alt={IMAGE_PATHS.CORONAL_VIEW_4.alt}
              />
            </div>
          </div>
          <Typography variant="body1">
            <b>Findings:</b>
          </Typography>
          <ul className={classes.list}>
            <li>1-cm node identified (yellow arrows) with uptake not greater than or equal to marrow (purple arrow)</li>
            <li>Image interpretation: negative, biopsy confirmed benign</li>
          </ul>
          <div className={classes.headerRow}>
            <Typography variant="h5" className={classes.title}>
              Please review the images and findings below
            </Typography>
            <Typography variant="h5" className={classNames(classes.title, classes.subtitle)}>
              See arrows for the location of the lesions and review findings
            </Typography>
          </div>
          <div className={classNames(classes.yellowBox, classes.rowWrapper)}>
            <div className={classes.row}>
              <Typography variant="body1" className={classes.rowTitle}>
                <b>Cranial to caudal view</b>
              </Typography>
              <img
                className={classes.image}
                src={`${BASE_URL}${IMAGE_PATHS.CRANIAL_CAUDAL_ARROWS.path}`}
                alt={IMAGE_PATHS.CRANIAL_CAUDAL_ARROWS.alt}
              />
            </div>
            <div className={classNames(classes.row, classes.columnWrapper)}>
              <div className={classes.column}>
                <Typography variant="body1" className={classes.rowTitle}>
                  <b>Coronal view</b>
                </Typography>
                <img
                  className={classes.smallImage}
                  src={`${BASE_URL}${IMAGE_PATHS.CORONAL_VIEW_3.path}`}
                  alt={IMAGE_PATHS.CORONAL_VIEW_3.alt}
                />
              </div>
              <div className={classes.column}>
                <Typography variant="body1" className={classes.rowTitle}>
                  <b>Sagittal view</b>
                </Typography>
                <img
                  className={classes.image}
                  src={`${BASE_URL}${IMAGE_PATHS.SAGITTAL_VIEW_4.path}`}
                  alt={IMAGE_PATHS.SAGITTAL_VIEW_4.alt}
                />
              </div>
            </div>
          </div>
          <Typography variant="body1">
            <b>Findings:</b>
          </Typography>
          <ul className={classes.list}>
            <li>3- to 5-mm retroperitoneal nodes (yellow arrows), significantly greater than blood pool visually</li>
            <li>
              Image interpretation: suspicious for prostate cancer (especially considering nodes in suspicious
              groupings); compare to sagittal midline spine; since appear similar to marrow even at this size, consider
              malignant; quantitation added confidence
            </li>
          </ul>
          <FindingsPanel trainingId={1} module="axuminInterpretation" page={15} />
        </div>
      </div>
      <LearningCheckQuestions
        module="axuminInterpretation"
        page={15}
        setNextDisabled={setNextDisabled}
        trainingId={1}
      />
    </>
  );
};

export default InterpretationPage15;
